// Styles globaux pour l'application Tanzeel
// Ce fichier contient tous les styles réutilisables dans l'application
import { formatShortDateInTz, formatTimeInTz, getAppTimezone } from '../utils/dateTime';

/** Options de formatage HH:MM (24 h) utilisées par les helpers ci-dessous. */
const TIME_HHMM: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: false };

export const GLOBAL_STYLES = {
  // Style pour les boutons تعديل (Edit)
  editButton: {
    padding: '6px 12px',
    backgroundColor: '#eff6ff', // Bleu très clair pour meilleur contraste
    border: '1px solid #3b82f6', // Bordure bleue plus visible
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
    color: '#1e40af', // Bleu foncé pour meilleure visibilité
    fontWeight: '500' as const, // Police plus visible
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap' as const,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    minHeight: '32px',
    maxHeight: '32px',
    lineHeight: '1'
  },

  // Style de survol pour les boutons تعديل (Edit)
  editButtonHover: '#dbeafe', // Bleu légèrement plus foncé au survol

  // Style pour les boutons حذف (Delete)
  deleteButton: {
    padding: '6px 12px',
    backgroundColor: '#fef2f2',
    border: '1px solid #ef4444', // Bordure rouge plus visible
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
    color: '#b91c1c', // Rouge plus foncé pour meilleur contraste
    fontWeight: '500' as const, // Police plus visible
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap' as const,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    minHeight: '32px',
    maxHeight: '32px',
    lineHeight: '1'
  },

  // Style de survol pour les boutons حذف (Delete)
  deleteButtonHover: '#fee2e2',
  
  // Style pour les boutons تفعيل/تعطيل (Toggle Status)
  toggleButton: (isActive: boolean) => ({
    padding: '6px 12px',
    backgroundColor: isActive ? '#fef2f2' : '#f0fdf4',
    border: isActive ? '1px solid #ef4444' : '1px solid #22c55e', // Bordures plus visibles
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
    color: isActive ? '#b91c1c' : '#15803d', // Couleurs plus foncées pour meilleur contraste
    fontWeight: '500' as const, // Police plus visible
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap' as const,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    minHeight: '32px',
    maxHeight: '32px',
    lineHeight: '1'
  }),

  // Style de survol pour les boutons تفعيل/تعطيل (Toggle Status)
  // Retourne la couleur de survol selon l'état actif/inactif
  toggleButtonHover: (isActive: boolean) => (isActive ? '#fee2e2' : '#dcfce7'),

  // Style pour le bouton تعطيل (Disable) spécifiquement
  disableButton: {
    padding: '6px 12px',
    backgroundColor: '#fef2f2',
    border: '1px solid #ef4444', // Bordure rouge plus visible
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
    color: '#b91c1c', // Rouge plus foncé pour meilleur contraste
    fontWeight: '500' as const, // Police plus visible
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap' as const,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    minHeight: '32px',
    maxHeight: '32px',
    lineHeight: '1'
  },

  // Style de survol pour le bouton تعطيل (Disable)
  disableButtonHover: '#fee2e2',
  
  // Style pour les badges de statut
  statusBadge: (isActive: boolean) => ({
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: isActive ? '12px' : '10px',
    fontWeight: '500',
    backgroundColor: isActive ? '#dcfce7' : '#fee2e2',
    color: isActive ? '#166534' : '#991b1b',
    whiteSpace: 'nowrap' as const,
    display: 'inline-block'
  }),

  // Style pour les boutons principaux (Primary buttons)
  primaryButton: {
    padding: '12px 24px',
    backgroundColor: '#2196f3',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },

  // Style pour les boutons secondaires (Secondary buttons)
  secondaryButton: {
    padding: '12px 24px',
    backgroundColor: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },

  // Style pour les boutons de danger (Danger buttons)
  dangerButton: {
    padding: '12px 24px',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },

  // Style pour les boutons de succès (Success buttons)
  successButton: {
    padding: '12px 24px',
    backgroundColor: '#16a34a',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },

  // Style pour les cartes (Cards)
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid #f0f0f0',
    transition: 'all 0.2s ease'
  },

  // Style pour les en-têtes de section
  sectionHeader: {
    color: '#424242',
    fontSize: '28px',
    fontWeight: '600',
    margin: 0,
    marginBottom: '30px'
  },

  // Style pour les en-têtes de tableau
  tableHeader: {
    padding: '16px',
    textAlign: 'center' as const,
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    borderBottom: '2px solid #e5e7eb'
  },

  // Style pour les cellules de tableau
  tableCell: {
    padding: '16px',
    textAlign: 'center' as const,
    color: '#374151'
  },

  // Style pour les inputs de formulaire
  formInput: {
    width: '100%',
    padding: '12px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '16px',
    backgroundColor: 'white',
    transition: 'border-color 0.2s ease',
    boxSizing: 'border-box' as const
  },

  // Style pour les labels de formulaire
  formLabel: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '500',
    color: '#374151',
    fontSize: '14px'
  },

  // Style pour les modales
  modal: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },

  modalContent: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto' as const,
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  },

  // Styles pour les dashboards (Admin et Teacher)
  dashboard: {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f3e8ff 0%, #dcfce7 100%)',
      fontFamily: 'Cairo, Arial, sans-serif'
    } as React.CSSProperties,
    
    nav: {
      background: 'white',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      borderBottom: '4px solid #9333ea',
      padding: '0 1rem'
    },
    
    navContent: {
      maxWidth: '1280px',
      margin: '0 auto',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: '64px'
    },
    
    logo: {
      width: '48px',
      height: '48px',
      marginLeft: '12px',
      borderRadius: '50%',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      border: '2px solid #9333ea'
    },
    
    title: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: '#9333ea'
    },
    
    navButtons: {
      display: 'flex',
      gap: '16px'
    },
    
    navButton: {
      padding: '12px 24px',
      borderRadius: '6px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontSize: '18px',
      fontWeight: '500'
    },
    
    navButtonActive: {
      background: '#9333ea',
      color: 'white'
    },
    
    navButtonInactive: {
      background: '#e5e7eb',
      color: '#374151'
    },
    
    logoutButton: {
      background: '#ef4444',
      color: 'white'
    },
    
    mainContent: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '32px 16px'
    },
    
    welcomeCard: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '32px'
    },
    
    welcomeIcon: {
      fontSize: '2rem',
      marginLeft: '16px'
    },
    
    welcomeTitle: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0
    },
    
    welcomeSubtitle: {
      color: '#6b7280',
      margin: '8px 0 0 0'
    },
    
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '24px',
      marginBottom: '32px'
    },
    
    statCard: {
      display: 'flex',
      alignItems: 'center',
      padding: '24px',
      borderRight: '4px solid'
    },
    
    statIcon: {
      fontSize: '1.5rem',
      marginLeft: '16px'
    },
    
    statLabel: {
      color: '#6b7280',
      fontSize: '14px',
      margin: 0
    },
    
    statValue: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0
    },
    
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      background: 'white'
    } as React.CSSProperties,
    
    tableHeader: {
      background: '#f9fafb',
      padding: '16px',
      textAlign: 'right' as const,
      fontSize: '12px',
      fontWeight: '500',
      color: '#6b7280',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em'
    } as React.CSSProperties,
    
    tableCell: {
      padding: '16px',
      borderBottom: '1px solid #e5e7eb',
      fontSize: '14px'
    },
    
    tableRow: {
      background: 'white'
    },
    
    formGroup: {
      marginBottom: '16px'
    },
    
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '8px'
    },
    
    input: {
      width: '100%',
      padding: '12px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '16px',
      backgroundColor: 'white',
      transition: 'border-color 0.2s ease',
      boxSizing: 'border-box' as const
    },
    
    textarea: {
      width: '100%',
      padding: '12px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '16px',
      backgroundColor: 'white',
      transition: 'border-color 0.2s ease',
      boxSizing: 'border-box' as const,
      minHeight: '100px',
      resize: 'vertical' as const
    },
    
    select: {
      width: '100%',
      padding: '12px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '16px',
      backgroundColor: 'white',
      transition: 'border-color 0.2s ease',
      boxSizing: 'border-box' as const
    },
    
    button: {
      padding: '12px 24px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.3s ease'
    },
    
    primaryButton: {
      background: '#10b981',
      color: 'white'
    },
    
    secondaryButton: {
      background: '#6b7280',
      color: 'white'
    },
    
    dangerButton: {
      background: '#ef4444',
      color: 'white'
    },
    
    successButton: {
      background: '#16a34a',
      color: 'white'
    },
    
    warningButton: {
      background: '#f59e0b',
      color: 'white'
    },
    
    infoButton: {
      background: '#3b82f6',
      color: 'white'
    },
    
    // Styles supplémentaires pour TeacherDashboard
    card: {
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      padding: '24px',
      marginBottom: '32px'
    },
    
    buttonGroup: {
      display: 'flex',
      gap: '16px',
      marginTop: '24px'
    },
    
    flexButton: {
      flex: 1
    },
    
    // Styles pour les modales
    modal: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50
    } as React.CSSProperties,
    
    modalContent: {
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      width: '100%',
      maxWidth: '448px',
      margin: '0 16px'
    }
  }
};

// Constantes globales
export const GLOBAL_CONSTANTS = {
  // Spécialisations disponibles
  SPECIALIZATIONS: [
    'القرآن الكريم',
    'التفسير',
    'الحديث الشريف',
    'الفقه',
    'العقيدة',
    'السيرة النبوية'
  ],

  // Cercles gérés
  MANAGED_CIRCLES: [
    'حلقة الحفظ',
    'حلقة المراجعة', 
    'حلقة التفسير',
    'حلقة التجويد'
  ],

  // Niveaux d'études
  STUDY_LEVELS: [
    'الابتدائي',
    'المتوسط',
    'الثانوي',
    'الجامعي'
  ],

  // Statuts disponibles
  STATUS_OPTIONS: [
    { value: 'active', label: 'نشط' },
    { value: 'inactive', label: 'غير نشط' },
    { value: 'معلق', label: 'معلق' },
    { value: 'منسحب', label: 'منسحب' }
  ],

  // Permissions disponibles
  PERMISSIONS: [
    { key: 'students', label: 'إدارة الطلاب' },
    { key: 'teachers', label: 'إدارة الأساتذة' },
    { key: 'parents', label: 'إدارة أولياء الأمور' },
    { key: 'admins', label: 'إدارة المديرين' },
    { key: 'communication', label: 'إدارة التواصل' },
    { key: 'reports', label: 'إدارة التقارير' }
  ],

  // Types de messages
  MESSAGE_TYPES: [
    { key: 'announcement', label: 'إعلان عام' },
    { key: 'absence', label: 'إشعار غياب' },
    { key: 'meeting', label: 'دعوة اجتماع' },
    { key: 'congratulation', label: 'تهنئة بالإنجاز' }
  ],

  // Priorités de messages
  MESSAGE_PRIORITIES: [
    { key: 'low', label: 'منخفضة', color: '#16a34a' },
    { key: 'medium', label: 'متوسطة', color: '#f59e0b' },
    { key: 'high', label: 'عالية', color: '#dc2626' }
  ]
};

// Fonctions utilitaires globales
export const GLOBAL_UTILS = {
  // Fonction pour nettoyer le texte et corriger l'encodage
  cleanText: (text: string) => {
    if (!text) return '';
    
    // Décoder les entités HTML et corriger l'encodage
    let cleanedText = text
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&nbsp;/g, ' ')
      .replace(/&apos;/g, "'")
      .replace(/&#39;/g, "'");
    
    // Essayer de décoder les caractères UTF-8 mal encodés
    try {
      // Si le texte contient des caractères mal encodés, essayer de les corriger
      if (cleanedText.includes('??????')) {
        // Remplacer les caractères mal encodés par des caractères arabes par défaut
        cleanedText = '';
      }
      
      // Décoder les entités numériques HTML
      cleanedText = cleanedText.replace(/&#(\d+);/g, (match, dec) => {
        return String.fromCharCode(dec);
      });
      
      // Décoder les entités hexadécimales HTML
      cleanedText = cleanedText.replace(/&#x([0-9A-Fa-f]+);/g, (match, hex) => {
        return String.fromCharCode(parseInt(hex, 16));
      });
      
    } catch (error) {
      console.warn('Erreur lors du nettoyage du texte:', error);
    }
    
    return cleanedText;
  },

  // Fonction de formatage des dates (date seule)
  // ⚠️ Toutes ces fonctions affichent dans le fuseau de l'ASSOCIATION (`default_timezone`),
  // jamais dans celui du poste de l'utilisateur (cf. utils/dateTime).
  formatDate: (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return ''; // Vérifier si la date est valide
    return formatShortDateInTz(date, getAppTimezone(), 'fr-FR');
  },

  // Fonction de formatage de l'heure (heure seule, format 24h)
  formatTime: (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return ''; // Vérifier si la date est valide
    return formatTimeInTz(date, getAppTimezone(), 'fr-FR', TIME_HHMM);
  },

  // Fonction de formatage complet (date + heure)
  formatDateTime: (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return ''; // Vérifier si la date est valide

    const tz = getAppTimezone();
    return `${formatShortDateInTz(date, tz, 'fr-FR')} ${formatTimeInTz(date, tz, 'fr-FR', TIME_HHMM)}`;
  },

  // Fonction pour obtenir la date et l'heure séparément (pour l'affichage avec styles différents)
  getDateTimeParts: (dateString: string) => {
    if (!dateString) return { date: '', time: '', isValid: false };
    const dateObj = new Date(dateString);
    if (isNaN(dateObj.getTime())) return { date: '', time: '', isValid: false };

    const tz = getAppTimezone();
    return {
      date: formatShortDateInTz(dateObj, tz, 'fr-FR'),
      time: formatTimeInTz(dateObj, tz, 'fr-FR', TIME_HHMM),
      isValid: true,
    };
  },

  // Fonction pour obtenir le statut affiché
  getDisplayStatus: (status: string) => {
    if (!status) return '';
    
    switch (status.toLowerCase()) {
      case 'active':
        return 'نشط';
      case 'inactive':
        return 'غير نشط';
      case 'نشط':
        return 'نشط';
      case 'غير نشط':
        return 'غير نشط';
      case 'معلق':
        return 'معلق';
      case 'منسحب':
        return 'منسحب';
      default:
        return status;
    }
  },

  // Fonction pour convertir le statut d'un enseignant
  convertTeacherStatus: (teacher: any) => {
    let status = teacher.status;
    if (status === 'نشط') status = 'active';
    if (status === 'غير نشط') status = 'inactive';
    if (status === '1') status = 'active';
    if (status === '0') status = 'inactive';
    return { ...teacher, status };
  },

  // Fonction pour convertir le statut d'un parent
  convertParentStatus: (parent: any) => {
    let status = parent.status;
    if (status === 'نشط') status = 'active';
    if (status === 'غير نشط') status = 'inactive';
    if (status === '1') status = 'active';
    if (status === '0') status = 'inactive';
    return { ...parent, status };
  },

  // Fonction pour convertir le statut d'un administrateur
  convertAdminStatus: (admin: any) => {
    let status = admin.status;
    if (status === 'نشط') status = 'active';
    if (status === 'غير نشط') status = 'inactive';
    if (status === '1') status = 'active';
    if (status === '0') status = 'inactive';
    
    // Gérer les permissions
    let permissions = admin.permissions;
    
    if (typeof permissions === 'string') {
      if (permissions === 'all') {
        permissions = ['students', 'teachers', 'parents', 'admins', 'communication', 'reports'];
      } else {
        // Si c'est une chaîne avec des permissions séparées par des virgules
        permissions = permissions.split(',').map((p: string) => p.trim());
      }
    } else if (Array.isArray(permissions)) {
      // Vérifier si c'est un tableau contenant "all"
      if (permissions.length === 1 && permissions[0] === 'all') {
        permissions = ['students', 'teachers', 'parents', 'admins', 'communication', 'reports'];
      }
      // Sinon, garder le tableau tel quel
    } else {
      permissions = ['students', 'teachers', 'parents', 'admins', 'communication', 'reports'];
    }
    
    return { ...admin, status, permissions };
  },

  // Fonction pour valider les données d'un parent
  validateParentData: (data: any) => {
    const errors: any = {};
    
    if (!data.fullName.trim()) {
      errors.fullName = 'الاسم الكامل مطلوب';
    }
    
    if (!data.username.trim()) {
      errors.username = 'اسم المستخدم مطلوب';
    }
    
    if (!data.phone.trim()) {
      errors.phone = 'رقم الهاتف مطلوب';
    }
    
    if (!data.email.trim()) {
      errors.email = 'البريد الإلكتروني مطلوب';
    } else if (!/\S+@\S+\.\S+/.test(data.email)) {
      errors.email = 'البريد الإلكتروني غير صحيح';
    }
    
    return errors;
  },

  // Fonction pour obtenir les couleurs des types de messages
  getMessageTypeColors: (message: any) => {
    const priorityColors = {
      low: '#16a34a',
      medium: '#f59e0b',
      high: '#dc2626'
    };
    
    return {
      backgroundColor: priorityColors[message.priority as keyof typeof priorityColors] || '#6b7280',
      color: 'white',
      borderColor: priorityColors[message.priority as keyof typeof priorityColors] || '#6b7280'
    };
  },

  // Fonction pour obtenir le label de l'étudiant selon le genre
  // Retourne "الطالب" pour Male et "الطالبة" pour Female
  getStudentLabel: (gender?: 'Male' | 'Female' | string | null) => {
    if (!gender) {
      // Si le genre n'est pas défini, retourner le format inclusif par défaut
      return 'الطالب/ـة';
    }
    
    // Normaliser le genre (gérer les variations de casse)
    const normalizedGender = typeof gender === 'string' ? gender.trim() : '';
    
    if (normalizedGender.toLowerCase() === 'female' || normalizedGender === 'أنثى' || normalizedGender === 'Female') {
      return 'الطالبة';
    }
    
    if (normalizedGender.toLowerCase() === 'male' || normalizedGender === 'ذكر' || normalizedGender === 'Male') {
      return 'الطالب';
    }
    
    // Par défaut, retourner le format inclusif si le genre n'est pas reconnu
    return 'الطالب/ـة';
  }
};
